import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@/lib/api';

/**
 * Versorger, Einkaufspreise und Margen-Einstellung.
 *
 * Quelle der Wahrheit ist der Server (/api/suppliers, /api/supplier-prices,
 * /api/margin-settings). Es gibt bewusst KEINEN localStorage-Fallback: ist die
 * API nicht erreichbar, meldet der Hook einen Fehler, statt veraltete
 * Browser-Daten als aktuellen Stand auszugeben.
 */

const DEFAULT_MARGIN = {
  scope: 'global',
  default_margin_ct_kwh: 1.5,
  min_margin_ct_kwh: 0.5,
  max_margin_ct_kwh: 3.0,
};

// ---------------------------------------------------------------------------
// Normalisierung
// Postgres liefert NUMERIC als String ("9.2000") und DATE als Zeitstempel.
// Beides wird hier zu JS-Zahlen bzw. reinen Kalendertagen (YYYY-MM-DD), damit
// in der Oberflaeche und in der Kalkulation gerechnet werden kann.
// ---------------------------------------------------------------------------

const toNum = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const pad = (n) => String(n).padStart(2, '0');

/** Heutiger Kalendertag als YYYY-MM-DD (lokale Zeit, nicht UTC). */
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Macht aus einem beliebigen Datumswert einen reinen Kalendertag (YYYY-MM-DD).
 * Ein DATE-Feld ist immer Mitternacht in der Server-Zeitzone; nach UTC
 * umgerechnet kippt es je nach Offset auf den Vor- oder Folgetag. Wir runden
 * deshalb auf den naechstgelegenen Kalendertag zurueck.
 */
export const toDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const utc = new Date(d.getTime());
  if (utc.getUTCHours() >= 12) utc.setUTCDate(utc.getUTCDate() + 1);
  return utc.toISOString().slice(0, 10);
};

const normalizeSupplier = (s) => ({
  ...s,
  is_active: s.is_active !== false,
});

const normalizePrice = (p) => ({
  ...p,
  price_ct_kwh: toNum(p.price_ct_kwh),
  contract_duration_months: toNum(p.contract_duration_months),
  min_consumption_kwh: toNum(p.min_consumption_kwh),
  max_consumption_kwh: toNum(p.max_consumption_kwh),
  valid_from: toDateOnly(p.valid_from),
  valid_until: toDateOnly(p.valid_until),
  is_active: p.is_active !== false,
});

const normalizeMargin = (m) => ({
  ...DEFAULT_MARGIN,
  ...(m || {}),
  default_margin_ct_kwh: toNum(m?.default_margin_ct_kwh) ?? DEFAULT_MARGIN.default_margin_ct_kwh,
  min_margin_ct_kwh: toNum(m?.min_margin_ct_kwh) ?? DEFAULT_MARGIN.min_margin_ct_kwh,
  max_margin_ct_kwh: toNum(m?.max_margin_ct_kwh) ?? DEFAULT_MARGIN.max_margin_ct_kwh,
});

// ---------------------------------------------------------------------------
// Gueltigkeit von Einkaufspreisen (pure Helfer, auch ausserhalb des Hooks nutzbar)
// ---------------------------------------------------------------------------

/**
 * Status eines Einkaufspreises zu einem Stichtag.
 * @returns {'aktiv'|'geplant'|'abgelaufen'|'inaktiv'}
 */
export const getPriceStatus = (price, date = todayISO()) => {
  if (!price || price.is_active === false) return 'inaktiv';
  const from = toDateOnly(price.valid_from);
  const until = toDateOnly(price.valid_until);
  if (from && from > date) return 'geplant';
  if (until && until < date) return 'abgelaufen';
  return 'aktiv';
};

/**
 * Darf dieser Preis in eine Kalkulation einfliessen?
 *
 * Drei Bedingungen, die vorher teilweise gefehlt haben:
 *  1. Status aktiv (is_active + valid_from/valid_until am Stichtag)
 *  2. Kundenspezifische Sondertarife (company_id gesetzt) nur fuer genau diese Firma
 *  3. Verbrauchsband, falls ein Verbrauch uebergeben wird
 */
export const isPriceUsable = (price, { companyId = null, consumption = null, date = todayISO() } = {}) => {
  if (getPriceStatus(price, date) !== 'aktiv') return false;

  if (price.company_id != null && price.company_id !== '') {
    if (companyId == null || companyId === '') return false;
    if (String(price.company_id) !== String(companyId)) return false;
  }

  if (consumption != null && consumption !== '') {
    const c = Number(consumption);
    if (!Number.isNaN(c)) {
      const min = toNum(price.min_consumption_kwh);
      const max = toNum(price.max_consumption_kwh);
      if (min != null && c < min) return false;
      if (max != null && c > max) return false;
    }
  }

  return true;
};

/** Kundenspezifische Tarife zuerst, danach aufsteigend nach Einkaufspreis. */
const comparePrices = (a, b) => {
  if (a.company_id && !b.company_id) return -1;
  if (!a.company_id && b.company_id) return 1;
  return (toNum(a.price_ct_kwh) ?? Infinity) - (toNum(b.price_ct_kwh) ?? Infinity);
};

/**
 * Filtert eine Preisliste auf die in einer Kalkulation verwendbaren Preise.
 * Bewusst als pure Funktion exportiert, damit die Kalkulation dieselbe Logik
 * nutzt wie die Oberflaeche.
 */
export const filterUsablePrices = (prices, options = {}) =>
  (prices || []).filter((p) => isPriceUsable(p, options)).sort(comparePrices);

// ---------------------------------------------------------------------------

const readJson = async (resp) => {
  let data = null;
  try { data = await resp.json(); } catch { /* leerer Body */ }
  if (!resp.ok) throw new Error((data && data.error) || `Serverfehler (HTTP ${resp.status})`);
  return data;
};

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [prices, setPrices] = useState([]);
  const [marginSettings, setMarginSettings] = useState(DEFAULT_MARGIN);
  const [loading, setLoading] = useState(true);
  // Lade- und Schreibfehler sind zwei verschiedene Dinge und duerfen sich nicht
  // gegenseitig ueberschreiben:
  //  - loadError  => die angezeigten Daten sind unvollstaendig/nicht vorhanden
  //                  (Ganzseiten-Hinweis, "erneut versuchen")
  //  - writeError => die Daten stimmen, nur eine Aenderung wurde nicht
  //                  gespeichert (Hinweis lokal am Formular/an der Aktion)
  // Ein fehlgeschlagenes Speichern darf niemals wie "keine Daten" aussehen und
  // niemals Bedienelemente dauerhaft sperren.
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');

  const clearWriteError = useCallback(() => setWriteError(''), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [supResp, priceResp, marginResp] = await Promise.all([
        apiFetch('/api/suppliers'),
        apiFetch('/api/supplier-prices'),
        apiFetch('/api/margin-settings'),
      ]);
      const [supData, priceData, marginData] = await Promise.all([
        readJson(supResp),
        readJson(priceResp),
        readJson(marginResp),
      ]);
      setSuppliers(Array.isArray(supData) ? supData.map(normalizeSupplier) : []);
      setPrices(Array.isArray(priceData) ? priceData.map(normalizePrice) : []);
      setMarginSettings(normalizeMargin(marginData));
    } catch (err) {
      // Kein stiller Fallback: lieber leer + Fehlermeldung als falsche Zahlen.
      setLoadError(err.message || 'Versorgerdaten konnten nicht geladen werden');
      setSuppliers([]);
      setPrices([]);
      setMarginSettings(DEFAULT_MARGIN);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Versorger -----------------------------------------------------------

  const addSupplier = useCallback(async (data) => {
    try {
      const resp = await apiFetch('/api/suppliers', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name,
          contact_name: data.contact_name || null,
          contact_email: data.contact_email || null,
          contact_phone: data.contact_phone || null,
          notes: data.notes || null,
        }),
      });
      const created = normalizeSupplier(await readJson(resp));
      setSuppliers((prev) => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de')));
      // Nur den Schreibfehler zuruecksetzen: ein erfolgreiches Speichern sagt
      // nichts darueber aus, ob der letzte Ladevorgang funktioniert hat.
      setWriteError('');
      return created;
    } catch (err) {
      const msg = err.message || 'Versorger konnte nicht angelegt werden';
      setWriteError(msg);
      throw err instanceof Error ? err : new Error(msg);
    }
  }, []);

  const updateSupplier = useCallback(async (id, changes) => {
    // Der Endpunkt schreibt alle Spalten — deshalb den aktuellen Stand mergen,
    // sonst werden nicht uebergebene Felder auf NULL gesetzt.
    const current = suppliers.find((s) => String(s.id) === String(id)) || {};
    const body = {
      name: changes.name ?? current.name,
      contact_name: changes.contact_name ?? current.contact_name ?? null,
      contact_email: changes.contact_email ?? current.contact_email ?? null,
      contact_phone: changes.contact_phone ?? current.contact_phone ?? null,
      notes: changes.notes ?? current.notes ?? null,
      is_active: changes.is_active ?? current.is_active ?? true,
    };
    try {
      const resp = await apiFetch(`/api/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      const raw = await readJson(resp);
      // Der Server antwortet bei unbekannter ID mit 200 und leerem Body. Ohne
      // diese Pruefung meldeten wir "gespeichert", obwohl nichts gespeichert wurde.
      if (!raw || raw.id == null) {
        throw new Error('Der Versorger existiert nicht mehr. Bitte Seite neu laden.');
      }
      const saved = normalizeSupplier(raw);
      setSuppliers((prev) => prev.map((s) => (String(s.id) === String(id) ? saved : s)));
      setWriteError('');
      return saved;
    } catch (err) {
      const msg = err.message || 'Versorger konnte nicht gespeichert werden';
      setWriteError(msg);
      throw err instanceof Error ? err : new Error(msg);
    }
  }, [suppliers]);

  const getSupplierById = useCallback(
    (id) => suppliers.find((s) => String(s.id) === String(id)),
    [suppliers]
  );

  // --- Preise --------------------------------------------------------------

  const addPrice = useCallback(async (data) => {
    try {
      const resp = await apiFetch('/api/supplier-prices', {
        method: 'POST',
        body: JSON.stringify({
          supplier_id: toNum(data.supplier_id),
          company_id: toNum(data.company_id),
          label: data.label,
          price_ct_kwh: toNum(data.price_ct_kwh),
          price_type: data.price_type || 'fixed',
          contract_duration_months: toNum(data.contract_duration_months),
          min_consumption_kwh: toNum(data.min_consumption_kwh),
          max_consumption_kwh: toNum(data.max_consumption_kwh),
          valid_from: data.valid_from || todayISO(),
          valid_until: data.valid_until || null,
          conditions: data.conditions || null,
          source: data.source || 'Manuell',
        }),
      });
      const created = normalizePrice(await readJson(resp));
      setPrices((prev) => [created, ...prev]);
      setWriteError('');
      return created;
    } catch (err) {
      const msg = err.message || 'Preis konnte nicht gespeichert werden';
      setWriteError(msg);
      throw err instanceof Error ? err : new Error(msg);
    }
  }, []);

  /**
   * Es gibt (noch) keinen PUT-Endpunkt fuer /api/supplier-prices.
   * Ehrlich scheitern ist besser, als die Aenderung nur lokal anzuzeigen.
   */
  const updatePrice = useCallback(async () => {
    const msg = 'Preise koennen derzeit nicht bearbeitet werden — der Endpunkt PUT /api/supplier-prices/:id fehlt. Bitte Preis loeschen und neu anlegen.';
    setWriteError(msg);
    throw new Error(msg);
  }, []);

  const deletePrice = useCallback(async (id) => {
    try {
      const resp = await apiFetch(`/api/supplier-prices/${id}`, { method: 'DELETE' });
      await readJson(resp);
      // Der Server deaktiviert die Zeile (is_active = false); GET liefert sie
      // danach nicht mehr aus — lokal also ebenfalls entfernen.
      setPrices((prev) => prev.filter((p) => String(p.id) !== String(id)));
      setWriteError('');
    } catch (err) {
      const msg = err.message || 'Preis konnte nicht geloescht werden';
      setWriteError(msg);
      throw err instanceof Error ? err : new Error(msg);
    }
  }, []);

  const getPricesForSupplier = useCallback(
    (supplierId) => prices.filter((p) => String(p.supplier_id) === String(supplierId) && p.is_active),
    [prices]
  );

  /**
   * Preise, die fuer eine konkrete Firma kalkuliert werden duerfen:
   * allgemeine Preise plus die Sondertarife genau dieser Firma, jeweils nur
   * innerhalb ihrer Gueltigkeit (valid_from/valid_until) und optional passend
   * zum Verbrauchsband.
   */
  const getPricesForCompany = useCallback(
    (companyId, consumption, referenceDate) =>
      filterUsablePrices(prices, {
        companyId,
        consumption,
        date: referenceDate ? toDateOnly(referenceDate) : todayISO(),
      }),
    [prices]
  );

  // --- Marge ---------------------------------------------------------------

  const updateMarginSettings = useCallback(async (settings) => {
    try {
      const resp = await apiFetch('/api/margin-settings', {
        method: 'PUT',
        body: JSON.stringify({
          default_margin_ct_kwh: toNum(settings.default_margin_ct_kwh),
          min_margin_ct_kwh: toNum(settings.min_margin_ct_kwh),
          max_margin_ct_kwh: toNum(settings.max_margin_ct_kwh),
        }),
      });
      const saved = normalizeMargin(await readJson(resp));
      setMarginSettings(saved);
      setWriteError('');
      return saved;
    } catch (err) {
      const msg = err.message || 'Marge konnte nicht gespeichert werden';
      setWriteError(msg);
      throw err instanceof Error ? err : new Error(msg);
    }
  }, []);

  const calculateCustomerPrice = useCallback((supplierPriceCtKwh, customMargin) => {
    const base = toNum(supplierPriceCtKwh) ?? 0;
    const margin = toNum(customMargin) ?? marginSettings.default_margin_ct_kwh;
    return {
      supplierPrice: base,
      margin,
      customerPrice: base + margin,
    };
  }, [marginSettings]);

  return {
    suppliers,
    prices,
    marginSettings,
    loading,
    loadError,
    writeError,
    clearWriteError,
    // Alias fuer bestehende Aufrufer: "error" meint immer NUR den Ladefehler.
    // Schreibfehler duerfen niemals eine Seite als "nicht geladen" darstellen.
    error: loadError,
    reload: load,
    addSupplier,
    updateSupplier,
    getSupplierById,
    addPrice,
    updatePrice,
    deletePrice,
    getPricesForSupplier,
    getPricesForCompany,
    updateMarginSettings,
    calculateCustomerPrice,
  };
};

export default useSuppliers;
